import type { IaNavItem } from '~/composables/unified/useIaNavigation';

import { IaAudience, useIaAudience } from '~/composables/unified/useIaAudience';
import { useSchoolsSiteUrl } from '~/composables/unified/useSchoolsSiteUrl';
import { useSignupEntryContext } from '~/composables/unified/useSignupEntryContext';
import { useSignupLink } from '~/composables/unified/useSignupLink';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { useRegionSwitcher } from '~/composables/useRegionSwitcher';
import { useServerFlag } from '~/composables/useServerFlag';
import { CLICKFUNNEL_CONTACT_URL } from '~/constants/constants';
import { EVENTS } from '~/constants/events';
import {
  UNIFIED_ROUTE_ABOUT,
  UNIFIED_ROUTE_ARTICLE_ROOT,
  UNIFIED_ROUTE_CAREERS,
  UNIFIED_ROUTE_EXPLORE,
  UNIFIED_ROUTE_INDEX,
  UNIFIED_ROUTE_LEARN,
  UNIFIED_ROUTE_PRIVACY_POLICY,
  UNIFIED_ROUTE_TERMS,
} from '~/constants/routes/unified';
import { useConfigStore } from '~/stores/config';
import { entrySourceForPath } from '~/utils/unified/entrySourceForRoute';
import { ENTRY_CTA } from '~/utils/unified/entryTracking';

export const useUnifiedLandingNav = async () => {
  const { t } = useI18n();
  const route = useRoute();

  const { $analytics } = useNuxtApp();

  const { getFlag } = useFeatureFlags();
  // Acquire before the first await — afterwards the Nuxt instance context is gone
  // and useRegionSwitcher()'s useRuntimeConfig() / useSignupEntryContext()'s useState() throw,
  // and onMounted has no instance to register against.
  const { regionOptions: footerRegions } = useRegionSwitcher();
  const { signupEntryContext } = useSignupEntryContext();
  const { buildLink } = useSignupLink();
  const configStore = useConfigStore();
  const isMounted = ref(false);
  onMounted(() => {
    isMounted.value = true;
  });
  // Shares the page's server-resolved answer (see useServerFlag); started here because it
  // registers asyncData, which needs the instance context the first await drops.
  const hubsEnabledAnswer = useServerFlag('hubs-enabled');
  const iaAudienceAnswer = useIaAudience();
  // The Schools nav item is the front door to the schools product.
  const schoolsNavLinkAnswer = useSchoolsSiteUrl();
  const schoolsEnquiryEnabled = await getFlag('schools-enquiry-enabled');
  const institutionGuideEnabled = await getFlag('institution-guide-enabled');
  const hubsEnabled = await hubsEnabledAnswer;
  const iaAudience = await iaAudienceAnswer;
  const schoolsNavLink = await schoolsNavLinkAnswer;

  // The header renders before any page setup runs, so the path is all it knows on the server.
  // The lane it reads out of that path is the same one the page itself publishes, because both
  // read it from the same map ({@see entrySourceForPath}).
  const routeEntrySource = computed(() => entrySourceForPath(route.path, { hubsEnabled }));

  // Two-phase on purpose. A page publishes its entity from its own setup, which runs AFTER this
  // header has already been rendered on the server — so the entity exists in the hydration payload
  // but not in the server's HTML. Reading it during hydration would make the client disagree with
  // the markup, and Vue does not patch attribute mismatches: the DOM would keep the server's href
  // forever. Until mount the link is therefore built from the route alone, which resolves
  // identically at both ends; the entity refines it once the client owns the DOM. What a copied or
  // new-tabbed pre-hydration link loses is only the specific entity — never the lane.
  const signUpLink = computed(() => {
    const context = isMounted.value ? signupEntryContext.value : null;
    return buildLink({
      entrySource: context?.entrySource ?? routeEntrySource.value,
      entryCta: ENTRY_CTA.HEADER_SIGNUP,
      selectedEntity: context?.selectedEntity,
    });
  });

  const handleSignUpClick = () => {
    $analytics.track(EVENTS.UNIFIED_PUBLIC_CTA_CLICKED, {
      entrySource: String(route.name ?? 'unknown'),
      entryCta: ENTRY_CTA.HEADER_SIGNUP,
      page: route.name,
    });
  };

  // The consumer marketing destinations are gated to login on a schools
  // cluster, so its guest header offers only the landing and sign-in (the
  // schools landing itself trims further — see SchoolsPublicShell).
  // Whether an item is the page being looked at, sub-views included, as the coach nav reads it.
  const isCurrent = (to: string) => route.path === to || route.path.startsWith(`${to}/`);

  // The new information architecture's bar takes over wherever it applies; its items are its own
  // (useIaNavigation), all but the sign-in, which is this one so it tracks the same way.
  const iaNav = computed(() => iaAudience.value !== IaAudience.Off);

  const signInItem = computed<IaNavItem>(() => ({ label: t('unified.nav.signIn'), to: signUpLink.value, isSecondary: true, onClick: () => handleSignUpClick() }));

  const navItems = computed(() => [
    { label: t('unified.nav.home'), to: UNIFIED_ROUTE_INDEX, imagePath: '/images/illustrations/billboard.webp', isActive: isCurrent(UNIFIED_ROUTE_INDEX) },
    ...(configStore.isSchoolsCluster
      ? []
      : [
          { label: t('unified.nav.learn'), to: UNIFIED_ROUTE_LEARN, imagePath: '/images/illustrations/screen-pink.webp', isActive: isCurrent(UNIFIED_ROUTE_LEARN) },
          // Explore is the IA's home for the institution guide (and, in time, the other
          // directories), so it arrives with the guide's release flag.
          ...(institutionGuideEnabled
            ? [{ label: t('unified.nav.explore'), to: UNIFIED_ROUTE_EXPLORE, imagePath: '/images/illustrations/magnifying-glass.webp', isActive: isCurrent(UNIFIED_ROUTE_EXPLORE) }]
            : []),
          { label: t('unified.nav.careerAdvice'), to: UNIFIED_ROUTE_CAREERS, imagePath: '/images/illustrations/microphone.webp', isActive: isCurrent(UNIFIED_ROUTE_CAREERS) || isCurrent(UNIFIED_ROUTE_ARTICLE_ROOT) },
          ...(schoolsEnquiryEnabled
            ? [{ label: t('unified.nav.schools'), to: schoolsNavLink.value, imagePath: '/images/illustrations/stacked-books.webp' }]
            : []),
          { label: t('unified.nav.about'), to: UNIFIED_ROUTE_ABOUT, imagePath: '/images/illustrations/phone-heart.webp', isActive: isCurrent(UNIFIED_ROUTE_ABOUT) },
        ]),
    signInItem.value,
  ]);

  const upperFooterLinks = [
    { text: t('general.footerLinks.contactUs'), to: CLICKFUNNEL_CONTACT_URL, external: true },
    { text: t('general.footerLinks.careers'), to: 'https://www.linkedin.com/company/anywayai/jobs/', external: true },
  ];

  const lowerFooterLinks = [
    { text: t('general.footerLinks.termsAndConditions'), to: UNIFIED_ROUTE_TERMS, external: false },
    { text: t('general.footerLinks.privacyPolicy'), to: UNIFIED_ROUTE_PRIVACY_POLICY, external: false },
  ];

  return { navItems, iaNav, signInItem, upperFooterLinks, lowerFooterLinks, footerRegions };
};
